// Copyright © 2026 Alex Bruce. All rights reserved.
//
// Pick text that stays readable on a given fill.
//
// Wherever the app paints a label on a colour it chose — a phase bar, a site
// plan chip — the text colour cannot be fixed. White on a pastel is invisible
// and black on a deep teal is worse, and which one you get depends on a
// palette the user can edit.

/** Relative luminance per WCAG, from a #rrggbb string. */
export function luminance(hex: string): number {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i.exec(hex);
  if (!m) return 0;
  const channel = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(parseInt(m[1], 16))
       + 0.7152 * channel(parseInt(m[2], 16))
       + 0.0722 * channel(parseInt(m[3], 16));
}

/** Contrast ratio between two colours, per WCAG. 1 is identical, 21 is black on white. */
export function contrastRatio(a: string, b: string): number {
  const la = luminance(a), lb = luminance(b);
  const hi = Math.max(la, lb), lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Roughly where a colour stops being dark. Used to decide which way to push a
 * colour, not which text to put on it -- that question has an exact answer.
 */
export const READABLE_THRESHOLD = 0.40;

/**
 * Dark or light text for a fill, whichever can actually be read.
 *
 * This used to cut at a tuned luminance threshold, which quietly picked the
 * worse of the two options for mid-tone colours: the site palette's red
 * #fc8181 got white type at 2.4:1 when black would have given 8.6:1. A
 * threshold is a guess at a question that has an exact answer, so it asks the
 * answer instead and takes whichever side has more contrast.
 *
 * The old tuning survives this, because it was approximating the same curve:
 * amber still takes dark type, burnt orange still takes light.
 */
export function readableOn(hex: string, dark = "#0f1117", light = "#ffffff"): string {
  if (!/^#?[0-9a-f]{6}/i.test(hex)) return light;
  return contrastRatio(hex, dark) >= contrastRatio(hex, light) ? dark : light;
}

function parseHex(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i.exec(hex);
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : null;
}

const toHex = (rgb: number[]) =>
  "#" + rgb.map((c) => Math.round(Math.max(0, Math.min(255, c))).toString(16).padStart(2, "0")).join("");

/**
 * A colour pushed just far enough to be read on a surface, keeping its hue.
 *
 * The site palette is built for coloured shapes on a dark canvas, so the same
 * values used as *type* on a white drawing are far too pale -- a pale slate
 * label on a white sheet is a smudge. Picking plain black instead would read,
 * but then the label stops saying which element it belongs to, and on this
 * drawing the colour is half the information.
 *
 * So it is darkened toward black on a light surface and lightened toward white
 * on a dark one, stopping at the first step that clears the contrast bar. On a
 * light surface that is a multiply, which holds the hue well; a yellow stays
 * recognisably yellow, just dark enough to read.
 */
export function inkOn(color: string, surface: string, minRatio = 4.5): string {
  const rgb = parseHex(color);
  if (!rgb) return readableOn(surface);
  if (contrastRatio(color, surface) >= minRatio) return color;

  const towardWhite = luminance(surface) <= READABLE_THRESHOLD;
  const target = towardWhite ? 255 : 0;
  for (let step = 1; step <= 20; step++) {
    const t = step / 20;
    const next = toHex(rgb.map((c) => c + (target - c) * t));
    if (contrastRatio(next, surface) >= minRatio) return next;
  }
  // Nothing in the hue clears the bar, so legibility wins over identity.
  return towardWhite ? "#ffffff" : "#0f1117";
}
